import React from 'react';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import ConsultationContactForm from '@/components/ConsultationContactForm';
import { Button } from '@/components/ui/button';
import { Shield } from 'lucide-react';
import { Link } from 'react-router-dom';

const Cybersecurity: React.FC = () => {
  return (
    <div className="min-h-screen">
      <Navbar />
      
      <section className="bg-itmgmt-dark-blue text-white py-16 md:py-24">
        <div className="container-wide">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h1 className="heading-xl mb-6">
                Cybersecurity Solutions
              </h1>
              <p className="text-xl mb-8 text-gray-300">
                Protect your business from evolving cyber threats with our comprehensive 
                security solutions designed for small and mid-sized businesses.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Button asChild className="button-primary">
                  <Link to="/book?service=cybersecurity">Book a Consultation</Link>
                </Button>
              </div>
            </div>
            <div className="hidden lg:flex justify-center">
              <div className="bg-itmgmt-medium-blue bg-opacity-20 p-8 rounded-full">
                <Shield size={120} className="text-itmgmt-bright-blue" />
              </div>
            </div>
          </div>
        </div>
      </section>
      
      <section className="section-padding bg-white">
        <div className="container-wide">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
            <div>
              <h2 className="heading-lg text-itmgmt-dark-blue mb-6">Comprehensive Protection</h2>
              <p className="text-lg text-gray-600 mb-8">
                Our cybersecurity services provide multi-layered protection to safeguard your business data, 
                systems, and reputation from increasingly sophisticated cyber threats.
              </p>
              
              <div className="space-y-6">
                <div className="bg-itmgmt-light-gray p-6 rounded-lg">
                  <h3 className="font-bold text-itmgmt-dark-blue text-xl mb-3">Core Security Features</h3>
                  <ul className="space-y-3">
                    <li className="flex items-start">
                      <span className="text-itmgmt-bright-blue mr-2 font-bold">•</span>
                      <span>Endpoint Detection & Response (EDR)</span>
                    </li>
                    <li className="flex items-start">
                      <span className="text-itmgmt-bright-blue mr-2 font-bold">•</span>
                      <span>Threat Detection & Blocking</span>
                    </li>
                    <li className="flex items-start">
                      <span className="text-itmgmt-bright-blue mr-2 font-bold">•</span>
                      <span>Email Protection & Phishing Prevention</span>
                    </li>
                    <li className="flex items-start">
                      <span className="text-itmgmt-bright-blue mr-2 font-bold">•</span>
                      <span>Multi-Factor Authentication (MFA)</span>
                    </li>
                    <li className="flex items-start">
                      <span className="text-itmgmt-bright-blue mr-2 font-bold">•</span>
                      <span>Secure Remote Access & VPN Setup</span>
                    </li>
                    <li className="flex items-start">
                      <span className="text-itmgmt-bright-blue mr-2 font-bold">•</span>
                      <span>Compliance Assistance (HIPAA, SOC2)</span>
                    </li>
                  </ul>
                </div>
              </div>
            </div>
            
            <div>
              <h2 className="heading-lg text-itmgmt-dark-blue mb-6">Why Cybersecurity Matters</h2>
              <div className="space-y-6">
                <div className="flex">
                  <div className="bg-itmgmt-medium-blue rounded-full h-10 w-10 flex items-center justify-center shrink-0 mr-4">
                    <span className="text-white font-bold">1</span>
                  </div>
                  <div>
                    <h3 className="font-bold text-itmgmt-dark-blue text-lg mb-2">Protect Business Continuity</h3>
                    <p className="text-gray-600">
                      Prevent costly downtime and data loss that can severely impact your operations.
                    </p>
                  </div>
                </div>
                
                <div className="flex">
                  <div className="bg-itmgmt-medium-blue rounded-full h-10 w-10 flex items-center justify-center shrink-0 mr-4">
                    <span className="text-white font-bold">2</span>
                  </div>
                  <div>
                    <h3 className="font-bold text-itmgmt-dark-blue text-lg mb-2">Safeguard Sensitive Data</h3>
                    <p className="text-gray-600">
                      Protect customer information, financial data, and intellectual property from unauthorized access.
                    </p>
                  </div>
                </div>
                
                <div className="flex">
                  <div className="bg-itmgmt-medium-blue rounded-full h-10 w-10 flex items-center justify-center shrink-0 mr-4">
                    <span className="text-white font-bold">3</span>
                  </div>
                  <div>
                    <h3 className="font-bold text-itmgmt-dark-blue text-lg mb-2">Maintain Customer Trust</h3>
                    <p className="text-gray-600">
                      Demonstrate commitment to data security, enhancing your reputation with clients and partners.
                    </p>
                  </div>
                </div>
                
                <div className="flex">
                  <div className="bg-itmgmt-medium-blue rounded-full h-10 w-10 flex items-center justify-center shrink-0 mr-4">
                    <span className="text-white font-bold">4</span>
                  </div>
                  <div>
                    <h3 className="font-bold text-itmgmt-dark-blue text-lg mb-2">Meet Compliance Requirements</h3>
                    <p className="text-gray-600">
                      Ensure adherence to industry regulations and data protection standards relevant to your business.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
      
      <section className="section-padding bg-itmgmt-light-gray">
        <div className="container-wide">
          <div className="text-center mb-12">
            <h2 className="heading-lg text-itmgmt-dark-blue mb-4">Our Cybersecurity Approach</h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              We take a proactive, multi-layered approach to cybersecurity tailored to your specific business needs.
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="bg-white p-6 rounded-lg shadow-sm">
              <h3 className="font-bold text-itmgmt-dark-blue text-xl mb-4">1. Assess</h3>
              <p className="text-gray-600 mb-4">
                We conduct thorough security assessments to identify vulnerabilities in your systems and processes.
              </p>
              <ul className="space-y-2">
                <li className="flex items-center">
                  <span className="text-itmgmt-bright-blue mr-2 font-bold">•</span>
                  <span>Network Vulnerability Scanning</span>
                </li>
                <li className="flex items-center">
                  <span className="text-itmgmt-bright-blue mr-2 font-bold">•</span>
                  <span>Security Policy Review</span>
                </li>
                <li className="flex items-center">
                  <span className="text-itmgmt-bright-blue mr-2 font-bold">•</span>
                  <span>Risk Assessment</span>
                </li>
              </ul>
            </div>
            
            <div className="bg-white p-6 rounded-lg shadow-sm">
              <h3 className="font-bold text-itmgmt-dark-blue text-xl mb-4">2. Protect</h3>
              <p className="text-gray-600 mb-4">
                We implement comprehensive security solutions to protect against both external and internal threats.
              </p>
              <ul className="space-y-2">
                <li className="flex items-center">
                  <span className="text-itmgmt-bright-blue mr-2 font-bold">•</span>
                  <span>Next-Gen Endpoint Protection</span>
                </li>
                <li className="flex items-center">
                  <span className="text-itmgmt-bright-blue mr-2 font-bold">•</span>
                  <span>Email Security & Filtering</span>
                </li>
                <li className="flex items-center">
                  <span className="text-itmgmt-bright-blue mr-2 font-bold">•</span>
                  <span>Access Control Implementation</span>
                </li>
              </ul>
            </div>
            
            <div className="bg-white p-6 rounded-lg shadow-sm">
              <h3 className="font-bold text-itmgmt-dark-blue text-xl mb-4">3. Monitor</h3>
              <p className="text-gray-600 mb-4">
                We provide continuous monitoring to detect and respond to security incidents quickly.
              </p>
              <ul className="space-y-2">
                <li className="flex items-center">
                  <span className="text-itmgmt-bright-blue mr-2 font-bold">•</span>
                  <span>Real-time Threat Monitoring</span>
                </li>
                <li className="flex items-center">
                  <span className="text-itmgmt-bright-blue mr-2 font-bold">•</span>
                  <span>Security Log Analysis</span>
                </li>
                <li className="flex items-center">
                  <span className="text-itmgmt-bright-blue mr-2 font-bold">•</span>
                  <span>Incident Response Planning</span>
                </li>
              </ul>
            </div>
          </div>
          
          <div className="mt-12 text-center">
            <Button asChild className="button-primary">
              <Link to="/book?service=cybersecurity">Schedule a Security Assessment</Link>
            </Button>
          </div>
        </div>
      </section>
      
      <ConsultationContactForm />
      <Footer />
    </div>
  );
};

export default Cybersecurity;
